import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { CheckCircle, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../services/authentication/authContext';
import { cardClass, canvasBg, primaryText, secondaryText } from '../../styling/stylingHelper';

function ManagedCustomerPaymentReturn() {
  const { t } = useTranslation();
  const { backendUser, accountSettings, refreshProfile } = useAuth();
  const [waitingLong, setWaitingLong] = useState(false);
  const active = backendUser?.subscription_status === 'ACTIVE';
  useEffect(() => {
    if (active) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      await refreshProfile(true);
      if (!cancelled) timer = setTimeout(() => void check(), 3000);
    };
    void check();
    const timeout = setTimeout(() => setWaitingLong(true), 60000);
    return () => { cancelled = true; clearTimeout(timer); clearTimeout(timeout); };
  }, [active, refreshProfile]);
  if (active) return <Navigate to={accountSettings.includes('account_setup') ? '/setup' : '/dashboard'} replace />;
  return <main className={`min-h-screen flex items-center justify-center p-5 sm:p-8 ${canvasBg} ${primaryText}`}>
    <section className={`w-full max-w-xl p-6 sm:p-8 text-center ${cardClass}`}>
      <h1 className="text-2xl font-semibold">{t('managedSignup.paymentPending')}</h1>
      <p role="status" className={`my-4 sm:my-5 ${secondaryText}`}>{t(waitingLong ? 'managedSignup.paymentDelayed' : 'managedSignup.paymentPendingDescription')}</p>
      <Link className="inline-flex items-center justify-center min-h-[44px] w-full sm:w-auto underline" to="/payment">{t('managedSignup.backToPayment')}</Link>
    </section>
  </main>;
}

function LegacySuccessPage() {
  const navigate = useNavigate();

  return (
    <div className="relative overflow-hidden min-h-screen bg-landingPageBG dark:bg-landingPageBG-dark text-white dark:text-white">
      <div className="flex flex-col justify-center items-center min-h-screen px-5 md:px-8 py-20">
        {/* Success Message */}
        <div className="w-full max-w-xl mx-auto">
          <div className="rounded-2xl border border-white/10 dark:border-white/10 p-8 bg-white/5 dark:bg-white/5 backdrop-blur-sm text-center">
            {/* Success Icon */}
            <div className="mb-6 flex justify-center">
              <div className="h-20 w-20 rounded-full bg-emerald-500/10 dark:bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-emerald-400 dark:text-emerald-400" />
              </div>
            </div>

            {/* Success Title */}
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white dark:text-white mb-4">
              Welcome to Culinary Cloud!
            </h1>

            {/* Success Message */}
            <p className="text-lg text-white/70 dark:text-white/70 mb-8">
              Congratulations on unlocking full access to Culinary Cloud. You now have access to all premium features to manage your restaurant operations more efficiently.
            </p>

            {/* Features Summary */}
            <div className="mb-8 p-4 bg-emerald-500/10 dark:bg-emerald-500/10 rounded-xl border border-emerald-500/30 dark:border-emerald-500/30">
              <p className="text-sm text-emerald-200 dark:text-emerald-200">
                Your subscription is now active. Start exploring all the powerful features available to you!
              </p>
            </div>

            {/* Go to Dashboard Button */}
            <button
              onClick={() => navigate('/')}
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base font-semibold bg-accent dark:bg-accent-dark text-white dark:text-white hover:bg-accent/90 dark:hover:bg-accent-dark/90 transition-all"
            >
              <Home className="w-5 h-5" />
              Go to Dashboard
            </button>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-12 pt-8 border-t border-white/10 dark:border-white/10 w-full max-w-xl mx-auto">
          <div className="text-center text-white/50 dark:text-white/50 text-sm space-y-2">
            <p>
              Need help getting started? Reach out to us at contact@culinarycloud.nl
            </p>
            <p>
              You can manage your subscription anytime from your settings page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SuccessPage() {
  const { backendUser } = useAuth();
  return backendUser?.managed_customer ? <ManagedCustomerPaymentReturn /> : <LegacySuccessPage />;
}
